import React, { useState } from "react";
import { alpha, Box, Button, Paper, Stack, Typography } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";

function WeekDayCard({ day, selected, onSelect }) {
  const statusLabel = day.overdueCount
    ? "Overdue"
    : day.dueCount === 0
      ? "Rest day"
      : `${day.doneCount}/${day.dueCount} Done`;
  return (
    <Paper
      component="button"
      type="button"
      onClick={onSelect}
      elevation={0}
      sx={{
        textAlign: "left",
        cursor: "pointer",
        minWidth: { xs: 98, md: 0 },
        minHeight: 162,
        borderRadius: "8px",
        border: "1px solid",
        borderWidth: selected ? 2 : 1,
        borderColor: day.overdueCount ? "warning.main" : day.isToday || selected ? "primary.main" : "divider",
        bgcolor: day.overdueCount ? alpha("#c47a22", 0.08) : day.isToday ? alpha("#17345d", 0.05) : "#fff",
        p: 1.5,
      }}
    >
      <Stack spacing={1.1}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", color: day.isToday ? "primary.main" : "text.secondary" }}>
            {day.fullLabel.slice(0, 3)}
          </Typography>
          {day.isToday && (
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main" }} />
          )}
        </Box>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: "text.primary" }}>
          {day.monthDay}
        </Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: day.overdueCount ? "warning.dark" : "text.secondary" }}>
          {statusLabel}
        </Typography>
        <Stack spacing={0.85}>
          {day.indicators.length ? (
            day.indicators.slice(0, 3).map((indicator) => (
              <Box key={indicator.id} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                {indicator.state === "done" ? (
                  <CheckRoundedIcon sx={{ fontSize: 16, color: "success.main" }} />
                ) : indicator.state === "overdue" ? (
                  <ErrorOutlineRoundedIcon sx={{ fontSize: 16, color: "warning.main" }} />
                ) : (
                  <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                )}
                <Typography sx={{ fontSize: 12.5, color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {indicator.shortLabel}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography sx={{ fontSize: 12.5, color: "text.disabled" }}>
              Rest day
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

function habitStatus(habit, isToday) {
  if (habit.completed) return "Done";
  if (habit.overdue) return "Planned time passed";
  if (habit.planned_time_label) return habit.planned_time_label;
  return isToday ? "Due today" : "Planned";
}

function habitActionLabel(habit, isToday) {
  if (habit.completed) return "Edit";
  if (!isToday) return "Edit";
  if (habit.overdue && habit.small_version_title) return "Try in Today";
  return "Start in Today";
}

export default function WeekPreviewCard({
  days = [],
  goalTitle,
  onHabitPress,
  onStartHabit,
  onTrySmallVersion,
  onAddHabit,
}) {
  const [selectedDate, setSelectedDate] = useState(() => days.find((day) => day.isToday)?.key || days[0]?.key || "");
  const selectedDay = days.find((day) => day.key === selectedDate) || days.find((day) => day.isToday) || days[0];

  return (
    <Paper elevation={0} sx={{ borderRadius: "8px", p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.25 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha("#17345d", 0.08),
            color: "primary.main",
          }}
        >
          <CalendarMonthOutlinedIcon fontSize="small" />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Schedule
          </Typography>
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            {goalTitle ? `For ${goalTitle}. See when this goal’s habits are planned.` : "See when your habits are planned."}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ overflowX: "auto", pb: 0.5 }}>
        <Box
          sx={{
            minWidth: { xs: 760, lg: "auto" },
            display: "grid",
            gridTemplateColumns: { xs: "repeat(7, 98px)", lg: "repeat(7, minmax(0, 1fr))" },
            gap: 1.5,
          }}
        >
          {days.map((day) => (
            <WeekDayCard
              key={day.key}
              day={day}
              selected={selectedDay?.key === day.key}
              onSelect={() => setSelectedDate(day.key)}
            />
          ))}
        </Box>
      </Box>

      {selectedDay && (
        <Box sx={{ borderTop: "1px solid", borderColor: "divider", mt: 2.5, pt: 2.25 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: "text.primary" }}>
            {selectedDay.isToday ? "Today’s Plan" : `${selectedDay.fullLabel} ${selectedDay.monthDay}`}
          </Typography>
          <Typography sx={{ mt: 0.35, fontSize: 14, color: "text.secondary" }}>
            {selectedDay.dueCount
              ? `${selectedDay.dueCount} habit${selectedDay.dueCount === 1 ? "" : "s"} planned. ${selectedDay.doneCount} done.`
              : "Nothing is scheduled for this day."}
          </Typography>

          {selectedDay.habits?.length ? (
            <Stack spacing={1.1} sx={{ mt: 1.75 }}>
              {selectedDay.habits.map((habit) => (
                <Box
                  key={`${selectedDay.key}-${habit.id}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    border: "1px solid",
                    borderColor: habit.overdue ? "warning.light" : "divider",
                    bgcolor: habit.overdue ? alpha("#c47a22", 0.08) : "background.paper",
                    borderRadius: 2,
                    px: 1.5,
                    py: 1.25,
                  }}
                >
                  <Box
                    onClick={() => onHabitPress?.(habit.id)}
                    sx={{ minWidth: 0, cursor: "pointer" }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 800 }} noWrap>
                      {habit.title}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: habit.overdue ? "warning.dark" : "text.secondary", fontWeight: habit.overdue ? 800 : 500 }}>
                      {habitStatus(habit, selectedDay.isToday)}
                    </Typography>
                    {habit.overdue && habit.small_version_title ? (
                      <Typography sx={{ fontSize: 12.5, color: "text.secondary" }} noWrap>
                        Keep-alive step: {habit.small_version_title}
                      </Typography>
                    ) : null}
                  </Box>
                  <Button
                    size="small"
                    variant={habit.overdue ? "contained" : "outlined"}
                    color={habit.overdue ? "warning" : "primary"}
                    onClick={() => {
                      if (!selectedDay.isToday || habit.completed) {
                        onHabitPress?.(habit.id);
                        return;
                      }
                      if (habit.overdue && habit.small_version_title) {
                        onTrySmallVersion?.(habit, selectedDay.key);
                        return;
                      }
                      onStartHabit?.(habit, selectedDay.key);
                    }}
                    sx={{ whiteSpace: "nowrap", fontWeight: 800 }}
                  >
                    {habitActionLabel(habit, selectedDay.isToday)}
                  </Button>
                </Box>
              ))}
            </Stack>
          ) : (
            <Box sx={{ mt: 1.75 }}>
              <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 1 }}>
                Nothing is scheduled for this day.
              </Typography>
              <Button variant="contained" onClick={onAddHabit}>
                Add a habit
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
}
